import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import App from './App.vue'
import './style.css'

// 导入页面组件
import Home from './views/Home.vue'
import Games from './views/Games.vue'
import GameDetail from './views/GameDetail.vue'
import News from './views/News.vue'
import About from './views/About.vue'
import User from './views/User.vue'
import Test from './views/Test.vue'
import Privacy from './views/Privacy.vue'
import Terms from './views/Terms.vue'
import Reviews from './views/Reviews.vue'
import Guides from './views/Guides.vue'

// 路由配置
const routes = [
  { 
    path: '/', 
    name: 'Home', 
    component: Home,
    meta: {
      title: 'CyberGame Hub - Ultimate PC Gaming Platform',
      description: 'Discover the future of PC gaming at CyberGame Hub. Play thousands of games instantly, join our vibrant community, and experience cutting-edge gaming technology.'
    }
  },
  { 
    path: '/games', 
    name: 'Games', 
    component: Games,
    meta: {
      title: 'Game Library - CyberGame Hub',
      description: 'Browse our extensive collection of PC games. Find your next favorite game with our advanced search and filter options.'
    }
  },
  { 
    path: '/game/:id', 
    name: 'GameDetail', 
    component: GameDetail,
    meta: {
      title: 'Game Details - CyberGame Hub',
      description: 'Play games instantly in your browser. Experience seamless gaming with our iframe integration technology.'
    }
  },
  { 
    path: '/news', 
    name: 'News', 
    component: News,
    meta: {
      title: 'Gaming News - CyberGame Hub',
      description: 'Stay updated with the latest gaming news, reviews, and industry insights from around the world.'
    }
  },
  { 
    path: '/about', 
    name: 'About', 
    component: About,
    meta: {
      title: 'About Us - CyberGame Hub',
      description: 'Learn more about CyberGame Hub, our mission, and the team behind the ultimate gaming platform.'
    }
  },
  { 
    path: '/user', 
    name: 'User', 
    component: User,
    meta: {
      title: 'User Center - CyberGame Hub',
      description: 'Manage your profile, view your game collection, and customize your gaming experience.'
    }
  },
  { 
    path: '/test', 
    name: 'Test', 
    component: Test,
    meta: {
      title: 'Data Test - CyberGame Hub',
      description: 'Test page for debugging data loading issues.'
    }
  },
  { 
    path: '/privacy', 
    name: 'Privacy', 
    component: Privacy,
    meta: {
      title: 'Privacy Policy - CyberGame Hub',
      description: 'Learn how we collect, use, and protect your personal information. Our comprehensive privacy policy covers data handling practices.'
    }
  },
  { 
    path: '/terms', 
    name: 'Terms', 
    component: Terms,
    meta: {
      title: 'Terms of Service - CyberGame Hub',
      description: 'Read our terms of service to understand the rules and guidelines for using CyberGame Hub platform.'
    }
  },
  { 
    path: '/reviews', 
    name: 'Reviews', 
    component: Reviews,
    meta: {
      title: 'Game Reviews & Ratings - CyberGame Hub',
      description: 'Read expert game reviews, community ratings, and detailed analysis of 500+ browser games. Find your next favorite game with trusted reviews from 2.5M+ players.'
    }
  },
  { 
    path: '/guides', 
    name: 'Guides', 
    component: Guides,
    meta: {
      title: 'Gaming Guides & Tips - CyberGame Hub',
      description: 'Master browser gaming with expert guides, strategies, and tips. From beginner tutorials to advanced techniques across all game genres.'
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// 路由守卫 - 更新页面标题和meta标签
router.afterEach((to, from) => {
  // 更新页面标题
  if (to.meta.title) {
    document.title = to.meta.title
  }
  
  // 更新meta描述
  if (to.meta.description) {
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', to.meta.description)
    }
    const ogDesc = document.querySelector('meta[property="og:description"]')
    if (ogDesc) ogDesc.setAttribute('content', to.meta.description)
    const twDesc = document.querySelector('meta[property="twitter:description"]')
    if (twDesc) twDesc.setAttribute('content', to.meta.description)
  }

  // 更新 OG title
  if (to.meta.title) {
    const ogTitle = document.querySelector('meta[property="og:title"]')
    if (ogTitle) ogTitle.setAttribute('content', to.meta.title)
    const twTitle = document.querySelector('meta[property="twitter:title"]')
    if (twTitle) twTitle.setAttribute('content', to.meta.title)
  }

  // 更新 canonical URL
  const canonical = document.querySelector('link[rel="canonical"]')
  if (canonical) {
    canonical.setAttribute('href', `https://cybergamehub.com${to.path}`)
  }
  const ogUrl = document.querySelector('meta[property="og:url"]')
  if (ogUrl) ogUrl.setAttribute('content', `https://cybergamehub.com${to.path}`)

  // 通知 Google Analytics 页面切换
  if (window.gtag) {
    window.gtag('config', 'G-45VN2LK7EM', {
      page_path: to.fullPath,
      page_title: to.meta.title || document.title
    })
  }

  // 通知 Google AdSense 自动广告：页面已变化，请重新扫描
  // 这是 SPA 中自动广告生效的关键
  if (from.path !== to.path) {
    try {
      const pushAd = () => {
        if (window.adsbygoogle) {
          window.adsbygoogle.push({
            google_ad_client: 'ca-pub-3014871134295110',
            enable_page_level_ads: true
          })
        }
      }
      // 等 DOM 更新完成后再触发
      setTimeout(pushAd, 300)
    } catch (e) {
      // 静默处理
    }
  }
})

const app = createApp(App)
app.use(router)
app.mount('#app') 